import type { Dispatch, SetStateAction } from 'react';
import { AppState, Role, User } from '../types/app';
import { api } from './api';

export type LoginCredentials = {
  identifier: string;
  password: string;
};

export type FleetManagerSignupInput = {
  username: string;
  email: string;
  password: string;
  fullName: string;
};

export type FleetManagerSignupResult = {
  message: string;
  userId: string;
  role: string;
  requestedRole: Role;
  onboardingRequired: boolean;
  nextStep?: string;
};

export type PasswordResetRole = 'admin' | 'super_admin' | 'fleet_manager' | 'law_enforcement';

export type AuthSession = {
  accessToken: string;
  refreshToken: string;
  user: User;
  role: Role;
};

const AUTH_STORAGE_KEY = 'responseops.auth';
const DEFAULT_ROUTE = '/fleet/dashboard';

const demoUsers: Record<Role, User> = {
  fleet_manager: {
    id: 'demo-fleet-manager',
    email: 'fleet.manager@dailymove.ng',
    name: 'Fleet Manager',
    initials: 'FM',
  },
  admin: {
    id: 'demo-admin',
    email: 'admin@dailymove.ng',
    name: 'Administrator',
    initials: 'A',
  },
  super_admin: {
    id: 'demo-super-admin',
    email: 'super.admin@dailymove.ng',
    name: 'Super Admin',
    initials: 'SA',
  },
  law_enforcement: {
    id: 'demo-law',
    email: 'law@dailymove.ng',
    name: 'Law Enforcement',
    initials: 'LE',
  },
};

export async function attemptLogin(credentials: LoginCredentials) {
  const payload = await api.post<unknown>('/auth/login', credentials);
  return normalizeAuthSession(payload);
}

export async function signupFleetManager(input: FleetManagerSignupInput) {
  const payload = await api.post<unknown>('/auth/signup', {
    username: input.username,
    email: input.email,
    password: input.password,
    full_name: input.fullName,
    role: 'fleet_manager',
  });

  return normalizeFleetManagerSignup(payload);
}

export function changePassword(currentPassword: string, newPassword: string) {
  return api.post<unknown>('/auth/password/change', {
    current_password: currentPassword,
    new_password: newPassword,
  });
}

export function verifySignupEmail(email: string, code: string) {
  return api.post<unknown>('/auth/verify-email', {
    email,
    code,
  });
}

export function resendSignupVerification(email: string) {
  return api.post<unknown>('/auth/verify-email/resend', { email });
}

export function requestPasswordReset(email: string, role: PasswordResetRole) {
  assertPasswordResetRole(role);
  return api.post<unknown>('/auth/password/forgot', { email });
}

export function resetPassword(email: string, code: string, newPassword: string, role: PasswordResetRole) {
  assertPasswordResetRole(role);
  return api.post<unknown>('/auth/password/reset', {
    email,
    code,
    new_password: newPassword,
  });
}

function assertPasswordResetRole(role: PasswordResetRole) {
  if (role !== 'admin' && role !== 'super_admin' && role !== 'fleet_manager' && role !== 'law_enforcement') {
    throw new Error('Password reset is only available for admin, super admin, fleet manager, and law enforcement accounts.');
  }
}

function normalizeFleetManagerSignup(payload: unknown): FleetManagerSignupResult {
  if (!payload || typeof payload !== 'object') {
    throw new Error('Invalid signup response');
  }

  const source = payload as Record<string, unknown>;
  const requestedRole = normalizeRole(readString(source, 'requested_role')) ?? 'fleet_manager';

  return {
    message: readString(source, 'message') ?? 'Signup successful. Please verify your email.',
    userId: readString(source, 'user_id') ?? '',
    role: readString(source, 'role') ?? 'passenger',
    requestedRole,
    onboardingRequired: readBoolean(source, 'onboarding_required') ?? requestedRole === 'fleet_manager',
    nextStep: readString(source, 'next_step'),
  };
}

export function loadStoredAuth(): AppState {
  const signedOutState: AppState = {
    accessToken: '',
    refreshToken: '',
    user: null,
    role: '',
    currentRoute: DEFAULT_ROUTE,
    demoMode: false,
  };

  try {
    const stored = window.localStorage.getItem(AUTH_STORAGE_KEY);

    if (!stored) {
      return signedOutState;
    }

    const session = normalizeAuthSession(JSON.parse(stored));

    return {
      accessToken: session.accessToken,
      refreshToken: session.refreshToken,
      user: session.user,
      role: session.role,
      currentRoute: DEFAULT_ROUTE,
      demoMode: false,
    };
  } catch {
    window.localStorage.removeItem(AUTH_STORAGE_KEY);
    return signedOutState;
  }
}

export function saveAuth(
  session: AuthSession,
  setAppState: Dispatch<SetStateAction<AppState>>,
) {
  window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
  setAppState((current) => ({
    ...current,
    accessToken: session.accessToken,
    refreshToken: session.refreshToken,
    user: session.user,
    role: session.role,
    currentRoute: isFleetManagerKycApproved(session) ? getSessionDefaultRoute(session.role) : '/fleet/onboarding',
    demoMode: false,
  }));
}

export function isFleetManagerKycApproved(session: Pick<AuthSession, 'role' | 'user'> | Pick<AppState, 'role' | 'user'>) {
  if (session.role !== 'fleet_manager') {
    return true;
  }

  if (!session.user) {
    return false;
  }

  if (session.user.onboardingRequired === false) {
    return true;
  }

  const status = normalizeApprovalStatus(session.user.kycStatus ?? session.user.onboardingStatus);
  return status === 'approved' || status === 'completed' || status === 'verified';
}

export function isSignupEmailVerified(session: Pick<AuthSession, 'role' | 'user'> | Pick<AppState, 'role' | 'user'>) {
  if (session.role !== 'fleet_manager') {
    return true;
  }

  return session.user?.emailVerified !== false;
}

export function clearAuth(setAppState: Dispatch<SetStateAction<AppState>>) {
  window.localStorage.removeItem(AUTH_STORAGE_KEY);
  setAppState((current) => ({
    ...current,
    accessToken: '',
    refreshToken: '',
    user: null,
    role: '',
    currentRoute: DEFAULT_ROUTE,
    demoMode: false,
  }));
}

export function enterDemo(setAppState: Dispatch<SetStateAction<AppState>>, role: Role = 'admin') {
  window.localStorage.removeItem(AUTH_STORAGE_KEY);
  setAppState((current) => ({
    ...current,
    accessToken: 'demo-access-token',
    refreshToken: 'demo-refresh-token',
    user: demoUsers[role],
    role,
    currentRoute: DEFAULT_ROUTE,
    demoMode: true,
  }));
}

function normalizeAuthSession(payload: unknown): AuthSession {
  if (!payload || typeof payload !== 'object') {
    throw new Error('Invalid login response');
  }

  const source = unwrapAuthPayload(payload as Record<string, unknown>);
  const accessToken = readString(source, 'accessToken')
    ?? readString(source, 'access_token')
    ?? readString(source, 'token');
  const refreshToken = readString(source, 'refreshToken')
    ?? readString(source, 'refresh_token')
    ?? '';
  const tokenPayload = decodeJwtPayload(accessToken);
  const userPayload = readObject(source, 'user') ?? tokenPayload;
  const role = normalizeRole(
    readString(source, 'requested_role')
    ?? readString(source, 'role')
    ?? readString(userPayload, 'role'),
  );

  if (!accessToken || !role) {
    throw new Error('Login response is missing session details');
  }

  const name = readString(userPayload, 'name')
    ?? readString(userPayload, 'fullName')
    ?? readString(userPayload, 'full_name')
    ?? readString(userPayload, 'email')
    ?? readString(source, 'email')
    ?? 'Operator';
  const email = readString(userPayload, 'email') ?? readString(source, 'email') ?? '';

  return {
    accessToken,
    refreshToken,
    role,
    user: {
      id: readString(userPayload, 'id')
        ?? readString(userPayload, '_id')
        ?? readString(userPayload, 'sub')
        ?? readString(source, 'user_id')
        ?? email,
      email,
      name,
      initials: readString(userPayload, 'initials') ?? getInitials(name),
      emailVerified: readBoolean(source, 'email_verified')
        ?? readBoolean(source, 'emailVerified')
        ?? readBoolean(source, 'is_email_verified')
        ?? readBoolean(source, 'verified')
        ?? readBoolean(userPayload, 'email_verified')
        ?? readBoolean(userPayload, 'emailVerified')
        ?? readBoolean(userPayload, 'is_email_verified')
        ?? readBoolean(userPayload, 'verified'),
      kycStatus: normalizeApprovalStatus(
        readString(source, 'kyc_status')
        ?? readString(source, 'kycStatus')
        ?? readString(source, 'verification_status')
        ?? readString(userPayload, 'kyc_status')
        ?? readString(userPayload, 'kycStatus')
        ?? readString(userPayload, 'verification_status'),
      ),
      onboardingRequired: readBoolean(source, 'onboarding_required')
        ?? readBoolean(source, 'onboardingRequired')
        ?? readBoolean(userPayload, 'onboarding_required')
        ?? readBoolean(userPayload, 'onboardingRequired')
        ?? readBoolean(source, 'kyc_required')
        ?? readBoolean(userPayload, 'kyc_required'),
      onboardingStatus: normalizeApprovalStatus(
        readString(source, 'onboarding_status')
        ?? readString(source, 'onboardingStatus')
        ?? readString(userPayload, 'onboarding_status')
        ?? readString(userPayload, 'onboardingStatus')
        ?? readString(source, 'status')
        ?? readString(userPayload, 'status'),
      ),
    },
  };
}

function getSessionDefaultRoute(role: Role | '') {
  if (role === 'admin' || role === 'super_admin') return '/admin/overview';
  if (role === 'law_enforcement') return '/incidents';
  return DEFAULT_ROUTE;
}

function unwrapAuthPayload(payload: Record<string, unknown>) {
  const data = readObject(payload, 'data');
  const session = readObject(payload, 'session');

  if (data) {
    return readObject(data, 'session') ?? data;
  }

  return session ?? payload;
}

function readObject(source: Record<string, unknown> | undefined, key: string) {
  const value = source?.[key];
  return value && typeof value === 'object' ? value as Record<string, unknown> : undefined;
}

function readString(source: Record<string, unknown> | undefined, key: string) {
  const value = source?.[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function readBoolean(source: Record<string, unknown> | undefined, key: string) {
  const value = source?.[key];
  return typeof value === 'boolean' ? value : undefined;
}

function normalizeRole(role: string | undefined): Role | undefined {
  if (
    role === 'super_admin'
    || role === 'admin'
    || role === 'fleet_manager'
    || role === 'law_enforcement'
  ) {
    return role;
  }

  return undefined;
}

function normalizeApprovalStatus(status: string | undefined) {
  return status?.trim().toLowerCase().replace(/[\s-]+/g, '_');
}

function getInitials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'OP';
}

function decodeJwtPayload(token: string | undefined) {
  if (!token) {
    return undefined;
  }

  try {
    const [, payload] = token.split('.');

    if (!payload) {
      return undefined;
    }

    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(window.atob(normalizedPayload));
    return decoded && typeof decoded === 'object' ? decoded as Record<string, unknown> : undefined;
  } catch {
    return undefined;
  }
}
